import type { TaskNode } from "../shared/types";
import { state } from "./state";
import {
  tbody, tlHead, grid, gridWrap, versionView, totalLabel, progressLabel, leftEmpty,
  populateBtn, tagList, PX_PER_DAY, COL_W, colEls, positionCard, iconBtn, copyToClipboard,
} from "./dom";
import { save } from "./persistence";
import { persistCollapse } from "./collapse";
import { persistPrefs } from "./prefs";
import { askChoice } from "./dialog";
import {
  locateOrNull, totalEstimate, disabledEstimate,
  progress, anyExpanded, leafIndexMap, computeMetrics, computeStreamRollups, computeShadowVersions, type NodeMetrics,
  filterVisibleSet,
} from "../shared/tree";
import { schedule } from "../shared/schedule";
import { rootIsSolo, trimPath, currentPath } from "../shared/paths";
import { fmt, niceStep } from "../shared/format";
import { ticketUrl } from "../shared/jira";
import { allTags, tagColor, tagMatchSet, tagUsagePaths, renameTag, dependencyExtras, dependencyCycles, dependencyLeafOrder, dependencyVersionConflicts } from "../shared/tags";
import {
  addChild, addSibling, removeTask, move, indent, outdent, toggleCollapse,
  toggleDisabled, toggleQuestions, togglePinned, cycleComplexity, setName, setEstimate, setTag, setJiraKey, addLink, removeLink,
  setDone, setDoneCascade, setDisabledCascade, setVersion, setComponent, setEstimateStream, resyncEstimateTotals,
} from "../shared/mutations";

const collapseBtn = document.getElementById("collapseBtn") as HTMLElement;
const notifyBar = document.getElementById("notifyBar") as HTMLElement;
// header "Estimate" dropdown: pick which estimate column(s) to show/edit (all / sum / a stream)
(document.getElementById("estViewSelect") as HTMLSelectElement).onchange = function () {
  state.estimateView = (this as HTMLSelectElement).value;
  commit();
};

// Tree filters (ephemeral, tree view only). The two toggles live in the View ▾ menu, under
// "Tasks"; the by-version dropdown stays in the header, next to the totals it changes.
(document.getElementById("viewPinned") as HTMLInputElement).onchange = function () {
  state.showPinnedOnly = (this as HTMLInputElement).checked;
  render();
};
(document.getElementById("viewHideDone") as HTMLInputElement).onchange = function () {
  state.hideDone = (this as HTMLInputElement).checked;
  render();
};
(document.getElementById("versionFilterSelect") as HTMLSelectElement).onchange = function () {
  state.versionFilter = (this as HTMLSelectElement).value;
  render();
};

/* ---------- commit + focus ---------- */
export function commit(): void {
  save();
  render();
}

// Mark a task to flash on the next render. Moves rebuild the whole table, so without
// this the row just silently appears somewhere else — hard to follow by eye.
function flash(id: string): void { state.flashId = id; }

function focusTarget(id: string | null, field: "name" | "est", select: boolean, stream?: string): void {
  if (!id) return;
  state.pendingFocus = { id, field, select, stream };
  render();
}
export function focusName(id: string | null, select = true): void { focusTarget(id, "name", select); }

// Focus a task that was just created, so Enter on it chains the next one (see `enterFromName`).
export function focusNewName(id: string | null): void {
  if (!id) return;
  state.freshId = id;
  focusName(id);
}

// Enter in a task title. By default it splits the outline into a new sibling — **above** the task
// when the caret sits at the very start, below it otherwise. But once you've *renamed* an existing
// task, Enter means "done": it finishes the edit rather than spawning a task nobody asked for.
// A just-created task (`state.freshId`) is the exception — naming it is part of creating it, so
// Enter there goes straight on to the next task, which keeps outline-style typing flowing.
function enterFromName(id: string, input: HTMLInputElement, base: string): void {
  if (state.freshId !== id && input.value.trim() !== base.trim()) {
    // Done renaming: blur and let `name.onblur` trim + save (the idiom the estimate inputs and
    // the project title already use). Leaving focus here read as "still editing"; now the cursor
    // goes with the edit. Blurring instead of save()-ing also stops one edit counting twice.
    input.blur();
    return;
  }
  const before = input.selectionStart === 0 && input.selectionEnd === 0;
  state.freshId = null;
  const nid = addSibling(state.tree, id, state.defaultEstimate, before);
  commit();
  focusNewName(nid);
}
// stream: which per-stream estimate input to focus (undefined → the first / only est input)
export function focusEst(id: string | null, stream?: string): void { focusTarget(id, "est", true, stream); }
function siblingId(id: string, dir: number): string | null {
  const i = state.visibleOrder.indexOf(id);
  return i < 0 ? null : state.visibleOrder[i + dir] ?? null;
}

// The next visible row (walking `dir`) that actually has an editable estimate input of the same
// column (`stream`) — skips group rows, which show a read-only roll-up rather than an input, so
// ↑/↓ steps through estimate fields only instead of jumping to a parent's title. null past the end.
function nextEstId(id: string, dir: number, stream?: string): string | null {
  const order = state.visibleOrder;
  const sel = stream ? `input.est[data-stream="${CSS.escape(stream)}"]` : "input.est";
  for (let i = order.indexOf(id) + dir; i >= 0 && i < order.length; i += dir) {
    const tr = tbody.querySelector(`tr[data-id="${order[i]}"]`);
    if (tr && tr.querySelector(sel)) return order[i];
  }
  return null;
}

/* ---------- render dispatch ---------- */
// render() coalesces: multiple render() calls in one task fold into a single rebuild.
// The common case is a handler doing commit()+focusName() (two renders) — and moves,
// which do move()+commit()+focusName() — now rebuild the table ONCE, not twice/thrice.
// A microtask (not rAF) runs before the next event, so the DOM stays current between
// keystrokes; pendingFocus is applied inside renderNow so focus still lands.
let renderQueued = false;
export function render(): void {
  if (renderQueued) return;
  renderQueued = true;
  queueMicrotask(() => { renderQueued = false; renderNow(); });
}

function renderNow(): void {
  if (state.rendering) return; // a blur fired mid-render; ignore the re-entrant call
  resyncEstimateTotals(state.tree, state.estimations); // keep estimate == Σ streams (seeds legacy/new leaves)
  state.rendering = true;
  try {
    const versionMode = state.view === "version";
    grid.style.display = versionMode ? "none" : "";
    versionView.hidden = !versionMode;
    if (versionMode) renderByVersion();
    else renderInner();
  } finally { state.rendering = false; }
  hideTagHover();
  hideCommentHover();
  hideEstHover(); // its anchor cell is gone — tbody was rebuilt
  updateCollapseBtn();
  updateDepWarn();
  syncEstViewSelect();
  syncFilterControls();
  syncViewToggle();
  persistCollapse(state.guid || "local", state.tree); // mirror collapse state into localStorage (no server touch)
  persistPrefs(state.guid || "local"); // mirror the view prefs (filters / columns / view mode) too
}

// Header tree-filter controls: reflect the ephemeral filter state. The 📌 button lights up when
// on; the version dropdown appears only when versions are defined and is rebuilt from them (a
// stale filter — its version was removed — resets to "All"). Both are for the tree view only.
function syncFilterControls(): void {
  const pin = document.getElementById("viewPinned") as HTMLInputElement;
  const hideDone = document.getElementById("viewHideDone") as HTMLInputElement;
  const wrap = document.getElementById("versionFilterWrap") as HTMLElement;
  const sel = document.getElementById("versionFilterSelect") as HTMLSelectElement;
  const treeMode = state.view === "tree";
  // Both filter the tree, so outside the WBS view they're greyed out in place rather than
  // dropped — the menu keeps its shape, and the setting is still there when you switch back.
  for (const box of [pin, hideDone]) {
    box.checked = box === pin ? state.showPinnedOnly : state.hideDone;
    box.disabled = !treeMode;
    const row = box.closest(".menu-check") as HTMLElement;
    row.classList.toggle("is-off", !treeMode);
    row.title = treeMode ? "" : "Only applies to the WBS view";
  }
  if (!treeMode || !state.versions.length) {
    wrap.hidden = true;
    if (!state.versions.length && state.versionFilter) state.versionFilter = ""; // no versions → drop filter
    return;
  }
  if (state.versionFilter && state.versionFilter !== "*" && !state.versions.includes(state.versionFilter)) state.versionFilter = "";
  wrap.hidden = false;
  sel.innerHTML = "";
  sel.appendChild(new Option("All", ""));
  sel.appendChild(new Option("All versions", "*")); // any versioned task (hide unversioned)
  state.versions.forEach((v) => sel.appendChild(new Option(v, v)));
  sel.value = state.versionFilter;
}

// Header view-mode toggle: a two-segment radio (WBS ⇄ Version). aria-pressed is the single
// source of truth — the CSS keys the fill, the weight and the label's visibility off it.
function syncViewToggle(): void {
  const versionMode = state.view === "version";
  document.getElementById("viewTreeBtn")!.setAttribute("aria-pressed", String(!versionMode));
  document.getElementById("viewVersionBtn")!.setAttribute("aria-pressed", String(versionMode));
}

// Render the bottom-of-screen notification stack. Today its only source is dependency
// warnings (cycles + version conflicts, when dependency mode is on); the region is built to
// host future notifications too. Recomputed each render since these are derived state.
const NOTIFY_MAX = 4;
export function updateDepWarn(): void {
  notifyBar.innerHTML = "";
  if (!state.countDeps) return;
  const items: { msg: string; detail: string }[] = [];
  for (const c of dependencyCycles(state.tree)) {
    const s = "Dependency cycle: " + c.join(" → ");
    items.push({ msg: s, detail: s });
  }
  for (const c of dependencyVersionConflicts(state.tree, state.versions)) {
    items.push({
      msg: `Version conflict: "${c.tag}" ships in ${c.featureVersion} but a ${c.dependentVersion} task needs it`,
      detail: `"${c.tag}" ships in ${c.featureVersion}, but ${c.dependentPath} (${c.dependentVersion}) depends on it`,
    });
  }
  const notice = (msg: string, detail: string) => {
    const el = document.createElement("div");
    el.className = "notify-item warn";
    if (detail) el.title = detail;
    const ico = document.createElement("span"); ico.className = "notify-ico"; ico.textContent = "⚠";
    const m = document.createElement("span"); m.className = "notify-msg"; m.textContent = msg;
    el.append(ico, m);
    notifyBar.appendChild(el);
  };
  items.slice(0, NOTIFY_MAX).forEach((it) => notice(it.msg, it.detail));
  const extra = items.length - NOTIFY_MAX;
  if (extra > 0) notice(`+${extra} more dependency issue${extra === 1 ? "" : "s"}`, items.slice(NOTIFY_MAX).map((i) => i.detail).join("\n"));
}

export function updateCollapseBtn(): void {
  collapseBtn.textContent = anyExpanded(state.tree) ? "Collapse all" : "Expand all";
}

interface Row { node: TaskNode; depth: number; wbs: string; path: string[]; disabled: boolean }

// The active tree filter (ephemeral): "Pinned only" and/or a chosen version, combined (AND).
// Returns null when no filter is on. `match(n)` is what a node must satisfy to be a filter hit
// (its ancestors show as context, its subtree shows in full — see filterVisibleSet).
function filterMatch(): ((n: TaskNode) => boolean) | null {
  const v = state.versionFilter, p = state.showPinnedOnly;
  if (!v && !p) return null;
  // v: "" = any · "*" = has any version (hide unversioned) · else a specific version
  const versionOk = (n: TaskNode) => !v || (v === "*" ? !!n.version : n.version === v);
  return (n) => versionOk(n) && (!p || n.pinned);
}

function flatten(): Row[] {
  const rows: Row[] = [];
  // a lone root is a wrapper: drop its WBS number so children number from 1 (no "1.x.y.z"),
  // mirroring how trimPath drops the lone root from displayed paths
  const solo = rootIsSolo(state.tree);
  const match = filterMatch();
  // filter active → only nodes in the visible set: matches, their parent chain (context), and the
  // full subtree of a matched group. All render in normal styling — no dimming — so a pinned group's
  // children and the ancestor chain stay easy to read; the 📌/version marker shows what matched.
  // Recurse ignoring collapse so a match under a collapsed context row stays reachable.
  const visible = match ? filterVisibleSet(state.tree, match) : null;
  // "Hide done": prune any node that is complete — a done leaf, or a fully-done group (whole
  // subtree gone). Partial groups stay, and their done descendants drop out on recursion.
  const done = state.hideDone ? computeMetrics(state.tree) : null;
  (function walk(list: TaskNode[], depth: number, prefix: string, ancestors: string[], ancestorDisabled: boolean) {
    list.forEach((node, i) => {
      const soloRoot = depth === 0 && solo;
      const wbs = soloRoot ? "" : prefix ? prefix + "." + (i + 1) : String(i + 1);
      const path = ancestors.concat(node.name);
      const disabled = ancestorDisabled || !!node.disabled;
      if (visible && !visible.has(node.id)) return; // pruned by the positive filter (subtree hidden)
      if (done && done.get(node.id)!.done) return; // hide-done: skip completed node + its subtree
      rows.push({ node, depth, wbs, path, disabled });
      // always honor collapse, even under a filter: a collapsed group hides its subtree (including
      // any matches below it) — the collapse toggle works the same in filtered and unfiltered views.
      const descend = node.children.length && !node.collapsed;
      if (descend) walk(node.children, depth + 1, wbs, path, disabled);
    });
  })(state.tree, 0, "", [], false);
  return rows;
}

// Per-node estimate of just the tasks currently SHOWN by an active filter (pinned / version):
// sum of the visible, active, not-hidden leaves in each node's subtree. null when no such filter
// is on. Lets group rows (and the total pill) read "10d of 127d" — shown work of the full total.
function filteredLeafRollups(): Map<string, number> | null {
  const match = filterMatch();
  if (!match) return null;
  const visible = filterVisibleSet(state.tree, match);
  const done = state.hideDone ? computeMetrics(state.tree) : null;
  const map = new Map<string, number>();
  function walk(node: TaskNode, ancDisabled: boolean): number {
    const disabled = ancDisabled || !!node.disabled;
    let sum = 0;
    if (node.children.length === 0) {
      if (visible.has(node.id) && !disabled && !(done && done.get(node.id)!.done)) sum = node.estimate || 0;
    } else for (const c of node.children) sum += walk(c, disabled);
    map.set(node.id, sum);
    return sum;
  }
  state.tree.forEach((n) => walk(n, false));
  return map;
}

// active leaves in display (pre-order) order — the fallback ordering when deps aren't counted
function collectActiveLeaves(tree: TaskNode[]): TaskNode[] {
  const out: TaskNode[] = [];
  (function walk(list: TaskNode[]) {
    for (const n of list) { if (n.disabled) continue; if (n.children.length === 0) out.push(n); else walk(n.children); }
  })(tree);
  return out;
}

// the visible active leaves in Gantt order, for the sub-plan timeline — undefined when no positive
// (pinned/version) filter is active (hide-done alone is handled inside schedule()).
function visibleLeafOrder(): TaskNode[] | undefined {
  const match = filterMatch();
  if (!match) return undefined;
  const visible = filterVisibleSet(state.tree, match);
  const base = state.countDeps ? dependencyLeafOrder(state.tree, state.depDocOrder) : collectActiveLeaves(state.tree);
  return base.filter((l) => visible.has(l.id) && !l.disabled);
}

// The set of node ids currently SHOWN (mirrors flatten's pruning: positive filter ∩ not hide-done-
// complete), or null when no filter is active. Used to scope batch operations to visible tasks.
export function visibleNodeIds(): Set<string> | null {
  const match = filterMatch();
  if (!match && !state.hideDone) return null;
  const inFilter = match ? filterVisibleSet(state.tree, match) : null;
  const done = state.hideDone ? computeMetrics(state.tree) : null;
  const out = new Set<string>();
  (function walk(list: TaskNode[]) {
    for (const n of list) {
      if ((!inFilter || inFilter.has(n.id)) && !(done && done.get(n.id)!.done)) out.add(n.id);
      walk(n.children);
    }
  })(state.tree);
  return out;
}

// count a group's descendants (all vs the ones in the visible set) — drives the scope dialog labels
function descendantCounts(node: TaskNode, vis: Set<string>): { all: number; visible: number } {
  let all = 0, visible = 0;
  (function walk(list: TaskNode[]) {
    for (const n of list) { all++; if (vis.has(n.id)) visible++; walk(n.children); }
  })(node.children);
  return { all, visible };
}

// Would "matching" vs "all" actually differ within scope, if the group's field becomes `newV`?
// Only when some in-scope descendant is on a value that is neither the group's OLD value (which
// "matching" repaints anyway) nor the NEW value (already there → a no-op). Otherwise both modes
// give the same result, so the prompt would be pointless. `scope` undefined → the whole subtree.
function matchingWouldDiffer(node: TaskNode, field: "version" | "component", scope: Set<string> | undefined, newV: string): boolean {
  const ref = node[field] || "", v = newV || "";
  let found = false;
  (function walk(list: TaskNode[]) {
    for (const n of list) {
      const d = n[field] || "";
      if ((!scope || scope.has(n.id)) && d !== ref && d !== v) found = true;
      walk(n.children);
    }
  })(node.children);
  return found;
}

// Ask how far a group batch op should reach when a filter hides some of its descendants.
// Returns: undefined = whole subtree (no filter / nothing hidden / user picked All), a Set = the
// visible descendants, or "cancel". `verb` labels the dialog (e.g. "Set version").
async function pickScope(node: TaskNode, verb: string): Promise<Set<string> | undefined | "cancel"> {
  const vis = visibleNodeIds();
  if (!vis) return undefined; // no active filter → whole subtree
  const { all, visible } = descendantCounts(node, vis);
  if (visible >= all) return undefined; // nothing hidden → whole subtree
  const pick = await askChoice({
    title: verb + " — how far?",
    message: `"${node.name}" has tasks hidden by the current filter. Apply to the visible tasks only, or the whole group?`,
    choices: [
      { label: `Visible only (${visible})`, value: "visible", primary: true },
      { label: `All (${all})`, value: "all" },
    ],
  });
  if (pick === null) return "cancel";
  return pick === "visible" ? vis : undefined;
}

/* ---------- header pills ---------- */
export function updateTotalLabel(): void {
  const total = totalEstimate(state.tree);
  // filtered tree view → "shown of whole" (e.g. estimate of the pinned tasks vs the full plan)
  const fr = state.view === "tree" ? filteredLeafRollups() : null;
  if (fr) {
    const shown = state.tree.reduce((s, n) => s + (fr.get(n.id) || 0), 0);
    if (shown !== total) { // "of Nd" is only meaningful when a subset is shown
      totalLabel.innerHTML = fmt(shown) + "d<span class=\"of-total\"> of " + fmt(total) + "d</span>";
      totalLabel.title = "Estimate of the shown (filtered) tasks / whole plan";
      return;
    }
  }
  const dis = disabledEstimate(state.tree);
  totalLabel.textContent = fmt(total) + "d total" + (dis > 0 ? " / " + fmt(dis) + "d extra" : "");
  totalLabel.title = dis > 0 ? "Active total / disabled (off) estimate" : "Active total";
}

export function updateProgress(): void {
  const p = progress(state.tree);
  if (!state.showDone || p.total <= 0) { progressLabel.hidden = true; return; }
  progressLabel.hidden = false;
  progressLabel.textContent = "✓ " + p.pct + "% (" + fmt(p.done) + "d of " + fmt(p.total) + "d)";
  // the pill reads done-of-total; the number people actually ask for is what's LEFT
  progressLabel.title = fmt(p.total - p.done) + "d left · " + fmt(p.done) + "d of " + fmt(p.total) + "d done";
}

/* ---------- text measuring (for auto-sizing columns) ---------- */
// One cached canvas per font weight, because the two auto-sized columns don't share one:
// `.wbs` is normal, but an assigned version renders through `.ver-select.set`, which is 600.
// Both are `font-variant-numeric: tabular-nums`, which measureText can't emulate — it draws
// every digit at the widest digit's width — so measure with the digits swapped for that one.
// Getting either wrong under-measures, which silently truncates the cell it was sizing.
// Ver cell chrome the label can't draw into: td padding 12 + select padding 4+18 + border 2.
const VER_CHROME = 36;
const measurers = new Map<number, { ctx: CanvasRenderingContext2D; digit: string }>();
function measurer(weight: number) {
  let m = measurers.get(weight);
  if (!m) {
    const ctx = document.createElement("canvas").getContext("2d");
    if (!ctx) return null;
    ctx.font = weight + " 12px " + getComputedStyle(document.body).fontFamily;
    const digits = [..."0123456789"];
    const digit = digits.reduce((a, b) => (ctx.measureText(b).width > ctx.measureText(a).width ? b : a));
    m = { ctx, digit };
    measurers.set(weight, m);
  }
  return m;
}
function textWidth(s: string, weight = 400): number {
  const m = measurer(weight);
  if (!m) return s.length * 7;
  return m.ctx.measureText(s.replace(/[0-9]/g, m.digit)).width;
}

// Every version label the Ver dropdown can show: the "—" blank, the configured
// versions, and any stale value still on a task (rendered as "x (?)").
function versionLabels(): string[] {
  const labels = new Set<string>(["—", ...state.versions]);
  (function walk(list: TaskNode[]) {
    for (const n of list) {
      if (n.version && !state.versions.includes(n.version)) labels.add(n.version + " (?)");
      walk(n.children);
    }
  })(state.tree);
  return [...labels];
}

// the ticket URL for a key under the plan's configured template ("" = no link)
export function jiraTicketUrl(key: string): string {
  return ticketUrl(state.jiraUrl, key);
}

// the linked Jira-key badge (a link when a ticket URL is configured); null when hidden/unset
// The Jira key badge (original look, linked when a ticket URL is set). ⌥/Alt-click swaps it for an
// input to edit the key (a plain click still opens the link); keyless leaves show a hover-revealed
// "+ key" affordance to add one. Shown only when "Show Jira keys" is on; keyless groups show nothing.
function buildJiraBadge(node: TaskNode): HTMLElement | null {
  if (!state.showJira) return null;
  if (!node.jiraKey && node.children.length) return null; // keyless group → nothing (Jira export is leaves-only)

  // swap `host` for a focused input; save on blur/Enter, revert on Escape
  const startEdit = (host: HTMLElement) => {
    const input = document.createElement("input");
    input.className = "jira-key-input";
    input.value = node.jiraKey;
    input.placeholder = "KEY";
    input.onclick = (e) => e.stopPropagation();
    let done = false;
    const finish = (saveIt: boolean) => {
      if (done) return; done = true;
      if (saveIt) { setJiraKey(state.tree, node.id, input.value.trim()); commit(); }
      else render(); // revert to the badge without saving
    };
    input.onblur = () => finish(true);
    input.onkeydown = (e) => {
      if (e.key === "Enter") { e.preventDefault(); finish(true); }
      else if (e.key === "Escape") { e.preventDefault(); finish(false); }
    };
    host.replaceWith(input);
    input.focus(); input.select();
  };

  if (!node.jiraKey) {
    const add = document.createElement("button");
    add.className = "jira-key-add";
    add.textContent = "+ key";
    add.title = "Add a Jira issue key";
    add.onclick = (e) => { e.stopPropagation(); startEdit(add); };
    return add;
  }

  const url = jiraTicketUrl(node.jiraKey);
  let kb: HTMLElement;
  if (url) {
    const a = document.createElement("a");
    a.href = url; a.target = "_blank"; a.rel = "noopener noreferrer";
    a.title = "Open Jira issue " + node.jiraKey + " (⌥-click to edit)";
    a.onclick = (e) => {
      e.stopPropagation();
      if (e.altKey) { e.preventDefault(); startEdit(a); } // ⌥-click → edit; plain click opens the link
    };
    kb = a;
  } else {
    const span = document.createElement("span");
    span.title = "Jira key " + node.jiraKey + " — click to edit";
    span.style.cursor = "pointer";
    span.onclick = (e) => { e.stopPropagation(); startEdit(span); }; // no link to open → click edits
    kb = span;
  }
  kb.className = "jira-key";
  kb.textContent = node.jiraKey;
  return kb;
}

/* ---------- timeline geometry ---------- */
/* ---------- dynamic estimate columns ---------- */
// One entry per estimate column to render between Comp and Deps. `index` is the stream's
// position in state.estimations (for computeStreamRollups lookups); left/width are filled by
// computeTimeline. Cases: 0 streams → one "plain" col; 1 stream → one "stream" col (no sum);
// ≥2 → per-stream cols + a "sum" col (the header dropdown can focus one stream or sum only).
interface EstCol { kind: "plain" | "stream" | "sum"; key: string | null; index: number; label: string; width: number; left: number }

function estColumns(): EstCol[] {
  const streams = state.estimations;
  const mk = (kind: EstCol["kind"], key: string | null, index: number, label: string, width: number): EstCol =>
    ({ kind, key, index, label, width, left: 0 });
  if (streams.length === 0) return [mk("plain", null, -1, "Est", COL_W.est)];
  if (streams.length === 1) return [mk("stream", streams[0], 0, streams[0], COL_W.est)];
  // ≥2 streams: the header "Estimate" dropdown picks what to show —
  //   "sum" → just the sum · a stream name → that one column + sum · else → all streams + sum
  const view = state.estimateView;
  const focus = streams.indexOf(view); // -1 unless view is a valid stream name
  let cols: EstCol[];
  if (view === "sum") cols = [];
  else if (focus >= 0) cols = [mk("stream", view, focus, view, COL_W.est)];
  else cols = streams.map((s, i) => mk("stream", s, i, s, COL_W.estStream));
  // when Σ is the ONLY column (sum-only), title it like the classic single estimate column
  const sumAlone = cols.length === 0;
  cols.push(mk("sum", null, -1, sumAlone ? "Est" : "Σ", sumAlone ? COL_W.est : COL_W.estSum));
  return cols;
}

// rebuild the header "Estimate" dropdown from the current streams and reflect estimateView;
// hidden unless there are ≥2 streams. Falls back to "all" if the saved value is now invalid.
function syncEstViewSelect(): void {
  const wrap = document.getElementById("estViewWrap") as HTMLElement;
  const sel = document.getElementById("estViewSelect") as HTMLSelectElement;
  const streams = state.estimations;
  if (streams.length < 2) { wrap.hidden = true; return; }
  wrap.hidden = false;
  if (state.estimateView !== "all" && state.estimateView !== "sum" && !streams.includes(state.estimateView)) {
    state.estimateView = "all"; // saved stream was renamed/removed
  }
  sel.innerHTML = "";
  sel.appendChild(new Option("All", "all"));
  sel.appendChild(new Option("Sum only", "sum"));
  streams.forEach((s) => sel.appendChild(new Option(s, s)));
  sel.value = state.estimateView;
}

// Rebuild the estimate <col> and <th> nodes (data-est) between colComp and colDeps to match
// `cols`. Keeps colgroup ↔ thead ↔ body cell counts in lockstep (fixed-layout requirement).
function syncEstColumns(cols: EstCol[]): void {
  const colgroup = colEls.deps.parentElement as HTMLElement;
  const headRow = document.querySelector("thead tr") as HTMLElement;
  const depsTh = headRow.querySelector("th.col-deps") as HTMLElement;
  colgroup.querySelectorAll("col[data-est]").forEach((e) => e.remove());
  headRow.querySelectorAll("th[data-est]").forEach((e) => e.remove());
  cols.forEach((c, i) => {
    const col = document.createElement("col");
    col.setAttribute("data-est", "");
    col.style.width = c.width + "px";
    colgroup.insertBefore(col, colEls.deps);
    const th = document.createElement("th");
    th.setAttribute("data-est", "");
    th.className = "sticky-col col-est" + (c.kind === "sum" ? " col-est-sum" : "");
    th.style.setProperty("--l-est", "var(--l-est-" + i + ")"); // resolved from #grid, see computeTimeline
    th.textContent = c.label;
    th.title = c.kind === "sum" ? "Σ — sum of all estimate streams (drives the timeline)"
      : c.kind === "stream" ? "Estimate: " + c.label : "Estimate (days)";
    headRow.insertBefore(th, depsTh);
  });
}

function computeTimeline() {
  const rows = flatten();
  // filtered view → lay out ONLY the visible leaves from day 0 (a sub-plan Gantt); otherwise the
  // full plan (dependency order when counting deps, else display order).
  const leafOrder = visibleLeafOrder() ?? (state.countDeps ? dependencyLeafOrder(state.tree, state.depDocOrder) : undefined);
  const { map: sched, segments } = schedule(state.tree, state.versions, leafOrder, state.hideDone);
  // size the axis to the ACTUAL laid bars, not the estimate total — empty/0-estimate tasks lay out
  // as a 1-day bar (barDur), so laidEnd can exceed totalEstimate; using total clipped the tail.
  const laidEnd = [...sched.values()].reduce((m, s) => Math.max(m, s.end), 0);
  const span = Math.max(laidEnd, 1);
  const stepDays = niceStep(span, Math.min(10, Math.ceil(span)));
  const stepPx = stepDays * PX_PER_DAY;
  const dayGrid = `repeating-linear-gradient(to right, #eef1f5 0, #eef1f5 1px, transparent 1px, transparent ${stepPx}px)`;
  const boundaries = segments.slice(0, -1).map((s) => {
    const x = s.end * PX_PER_DAY;
    return `linear-gradient(to right, transparent ${x - 1}px, rgba(124,58,237,0.45) ${x - 1}px, rgba(124,58,237,0.45) ${x}px, transparent ${x}px)`;
  });
  // done/not-done split line(s): done leaves lay out first, so within each contiguous block there's
  // a boundary where done work ends. Plain mode → one global boundary; version mode → one per band
  // (done-first is applied within each band). A band gets a line only when it has both done & open.
  const leafSlots: { version: string; done: boolean; end: number }[] = [];
  (function scanSplit(list: TaskNode[]) {
    for (const n of list) {
      if (n.disabled) continue;
      if (n.children.length === 0) { const s = sched.get(n.id); if (s) leafSlots.push({ version: n.version || "", done: !!n.done, end: s.end }); }
      else scanSplit(n.children);
    }
  })(state.tree);
  const boundaryOf = (slots: typeof leafSlots): number | null => {
    const done = slots.filter((l) => l.done), open = slots.filter((l) => !l.done);
    return done.length && open.length ? Math.max(...done.map((l) => l.end)) : null;
  };
  const splitDays = (segments.length <= 1
    ? [boundaryOf(leafSlots)]
    : segments.map((seg) => boundaryOf(leafSlots.filter((l) => l.version === seg.version)))
  ).filter((d): d is number => d != null);
  const splitLines = splitDays.map((d) => {
    const x = d * PX_PER_DAY;
    return `linear-gradient(to right, transparent ${x - 1}px, rgba(220,38,38,0.85) ${x - 1}px, rgba(220,38,38,0.85) ${x + 1}px, transparent ${x + 1}px)`;
  });
  const gridBg = [...splitLines, ...boundaries, dayGrid].join(", ");

  // WBS auto-sizes to the widest number actually shown (same approach as Ver below):
  // 20px of cell padding + 4px slack, since measureText ignores tabular-nums. The floor
  // keeps the "WBS" header from clipping; the ceiling stops a pathologically deep tree
  // from eating the Task column.
  const widestWbs = rows.reduce((m, r) => Math.max(m, textWidth(r.wbs)), 0);
  const wWbs = Math.round(Math.max(52, Math.min(160, widestWbs + 24)));
  const tagsW = state.showTags ? COL_W.tags : 0;
  colEls.tags.style.width = tagsW + "px";
  grid.classList.toggle("tags-hidden", !state.showTags);
  // Ver column auto-sizes to the widest label it can show. VER_CHROME is what the label
  // never gets to use: the td's padding (12) + the select's own padding, which holds the
  // dropdown arrow (4+18) + its border (2) = 36. The rest is deliberate headroom — the
  // label is measured, not laid out, so a font whose metrics differ from the measuring
  // canvas must not clip ("INSPOT 1.0.10" used to land 0.3px inside and truncate). Measured
  // at 600 because that's `.ver-select.set`, the weight an assigned version renders in.
  const widestVer = versionLabels().reduce((m, s) => Math.max(m, textWidth(s, 600)), 0);
  const verW = state.showVersions ? Math.round(Math.max(COL_W.ver, Math.min(300, widestVer + VER_CHROME + 20))) : 0;
  colEls.ver.style.width = verW + "px";
  grid.classList.toggle("versions-hidden", !state.showVersions);
  const compW = state.showComponents ? COL_W.comp : 0;
  colEls.comp.style.width = compW + "px";
  grid.classList.toggle("components-hidden", !state.showComponents);
  const depsW = state.countDeps ? COL_W.deps : 0;
  colEls.deps.style.width = depsW + "px";
  grid.classList.toggle("deps-hidden", !state.countDeps);
  const estCols = estColumns();
  const estW = estCols.reduce((s, c) => s + c.width, 0);
  // Every column except Task has a width of its own; Task takes what's left. With the Gantt on
  // that's just its default (the timeline soaks up the rest of the page and scrolls). With the
  // Gantt off there's nothing to fill the page, so the task names get the room instead of the
  // grid ending mid-screen against a band of empty background.
  const fixedW = COL_W.leaf + wWbs + COL_W.copy + tagsW + verW + compW + estW + depsW + COL_W.act;
  const wrapW = gridWrap ? gridWrap.clientWidth : 0;
  const taskW = state.showGantt ? COL_W.task : Math.max(COL_W.task, wrapW - fixedW - 1);
  colEls.task.style.width = taskW + "px";
  // dynamic estimate columns: assign each a left offset (accumulating from where the est
  // block starts), then rebuild the <col>/<th> nodes to match.
  const estStart = COL_W.leaf + wWbs + taskW + COL_W.copy + tagsW + verW + compW;
  let estAcc = estStart;
  // Publish each offset as --l-est-N on #grid (the cells read it via --l-est). The other columns
  // already work this way; the est cells used to carry an inline `left`, set once when the row was
  // built — so on resize the *header* moved (syncEstColumns rebuilds it) and the body cells stayed
  // behind, leaving the numbers adrift from their heading. One var per column, not one per cell.
  estCols.forEach((c, i) => { c.left = estAcc; estAcc += c.width; grid.style.setProperty("--l-est-" + i, c.left + "px"); });
  syncEstColumns(estCols);
  const stickyW = fixedW + taskW;
  const naturalTl = Math.ceil(span * PX_PER_DAY) + 16;
  const availTl = wrapW - stickyW - 1;
  // Gantt off → collapse the timeline column to 0 (its <col>, like the other hidden columns;
  // never display:none, that would shift the <col>→cell mapping) so the grid is the task table.
  const tlWidth = state.showGantt ? Math.max(naturalTl, availTl, 360) : 0;
  grid.classList.toggle("gantt-hidden", !state.showGantt);
  colEls.wbs.style.width = wWbs + "px";
  colEls.tl.style.width = tlWidth + "px";
  grid.style.width = stickyW + tlWidth + "px";
  grid.style.setProperty("--l-wbs", COL_W.leaf + "px");
  grid.style.setProperty("--l-task", COL_W.leaf + wWbs + "px");
  grid.style.setProperty("--l-copy", COL_W.leaf + wWbs + taskW + "px");
  grid.style.setProperty("--l-tags", COL_W.leaf + wWbs + taskW + COL_W.copy + "px");
  grid.style.setProperty("--l-ver", COL_W.leaf + wWbs + taskW + COL_W.copy + tagsW + "px");
  grid.style.setProperty("--l-comp", COL_W.leaf + wWbs + taskW + COL_W.copy + tagsW + verW + "px");
  grid.style.setProperty("--l-deps", estStart + estW + "px");
  grid.style.setProperty("--l-act", estStart + estW + depsW + "px");

  tlHead.innerHTML = "";
  if (!state.showGantt) return { rows, sched, span, tlWidth, gridBg, estCols }; // no column → no axis to draw
  const axis = document.createElement("div");
  axis.className = "tl-axis";
  for (let e = stepDays; e <= span + 0.0001; e += stepDays) {
    const tick = document.createElement("div");
    tick.className = "tick";
    tick.style.left = (e - stepDays) * PX_PER_DAY + "px";
    tick.style.width = stepDays * PX_PER_DAY + "px";
    tick.textContent = fmt(e) + "d";
    axis.appendChild(tick);
  }
  const lastE = Math.floor((span + 0.0001) / stepDays) * stepDays;
  if (span - lastE > 0.0001) {
    const tick = document.createElement("div");
    tick.className = "tick";
    tick.style.left = lastE * PX_PER_DAY + "px";
    tick.style.width = (span - lastE) * PX_PER_DAY + "px";
    tick.textContent = fmt(span) + "d";
    axis.appendChild(tick);
  }
  const labelSegments = segments.length === 1 && !segments[0].version ? [] : segments;
  labelSegments.forEach((s, i) => {
    const seg = document.createElement("div");
    seg.className = "ver-seg";
    seg.style.left = s.start * PX_PER_DAY + "px";
    seg.style.width = s.days * PX_PER_DAY + "px";
    seg.textContent = (s.version || "—") + " · " + fmt(s.days) + "d";
    seg.title = (s.version || "no version") + ": " + fmt(s.days) + "d (ends day " + fmt(s.end) + ")";
    axis.appendChild(seg);
    if (i < labelSegments.length - 1) {
      const bound = document.createElement("div");
      bound.className = "ver-bound";
      bound.style.left = s.end * PX_PER_DAY + "px";
      axis.appendChild(bound);
    }
  });
  splitDays.forEach((d) => {
    const split = document.createElement("div");
    split.className = "done-split";
    split.style.left = d * PX_PER_DAY + "px";
    split.title = "Done / to-do boundary — " + fmt(d) + "d done";
    axis.appendChild(split);
  });
  tlHead.appendChild(axis);

  return { rows, sched, span, tlWidth, gridBg, estCols };
}

// live-update derived views without rebuilding the table (estimate edits)
export function updateDerived(): void {
  const { sched, tlWidth, gridBg } = computeTimeline();
  const metrics = computeMetrics(state.tree); // O(n) once; replaces per-row locateOrNull (was O(n²))
  const deps = state.countDeps ? dependencyExtras(state.tree, metrics) : null;
  const streamRollups = state.estimations.length ? computeStreamRollups(state.tree, state.estimations) : null;
  tbody.querySelectorAll("tr").forEach((tr) => {
    const id = (tr as HTMLElement).dataset.id || "";
    const s = sched.get(id);
    const tdTl = tr.querySelector(".col-tl") as HTMLElement | null;
    if (tdTl) { tdTl.style.width = tlWidth + "px"; tdTl.style.background = gridBg; }
    const bar = tr.querySelector(".gantt-bar") as HTMLElement | null;
    if (bar && s) {
      const dur = s.end - s.start;
      bar.style.left = s.start * PX_PER_DAY + "px";
      bar.style.width = Math.max(dur * PX_PER_DAY, 3) + "px";
      bar.textContent = dur > 0 ? fmt(dur) + "d" : "";
    }
    const m = metrics.get(id);
    const isGroup = (tr as HTMLElement).classList.contains("group");
    const show = (v: number) => (isGroup && !v ? "" : fmt(v)); // group rows leave 0 roll-ups blank
    // refresh every read-only estimate span in the row: per-stream (by index), Σ (sum-val), and plain
    tr.querySelectorAll(".col-est .rollup").forEach((el) => {
      const span = el as HTMLElement;
      const si = span.dataset.streamIndex;
      if (si != null) { const vals = streamRollups?.get(id); if (vals) span.textContent = show(vals[+si] ?? 0); }
      else if (!m) { /* nothing */ }
      else if (span.classList.contains("sum-val")) fillSum(span, m.rollup, isGroup, true);
      else fillRollup(span, m.rollup); // group plain roll-up → "Nd"
    });
    // refresh an existing dep-extra value live (appear/disappear waits for the next full render)
    const deEl = tr.querySelector(".dep-extra") as HTMLElement | null;
    if (deEl) { const dx = deps?.get(id) ?? 0; deEl.textContent = dx > 0 ? "+" + fmt(dx) + "d" : ""; }
    // refresh an existing unestimated badge live (same convention: appear/disappear on full render)
    const ueEl = tr.querySelector(".col-est .unest") as HTMLElement | null;
    if (ueEl && m) ueEl.textContent = m.unestimated > 0 ? "+" + m.unestimated : "";
    // the group-total tooltip spells out done/left days — an estimate edit moves that split
    const tipTd = tr.querySelector(".col-est.est-tip") as HTMLElement | null;
    if (tipTd && m) applyEstTip(tipTd, tipTd.dataset.tipBase || "", m);
  });
  updateTotalLabel();
  updateProgress();
  updateDepWarn();
}

// a read-only roll-up/total number for an estimate cell; `streamIndex` tags per-stream
// spans so updateDerived can refresh them by index, and `extraCls` adds a modifier class.
// small amber suffix on a group's total, counting descendant tasks that are still open (active,
// not done) with no estimate yet — surfaces how much of a group is left to estimate.
// No `title`: the badge only ever sits in a cell that carries the est hover card, which already
// spells the count out — a native tooltip on top of the card would fight it.
function unestBadge(n: number): HTMLElement {
  const s = document.createElement("span");
  s.className = "unest";
  s.textContent = "+" + n;
  return s;
}

function rollupSpan(text: string, title: string, streamIndex: number | null, extraCls = ""): HTMLElement {
  const span = document.createElement("span");
  span.className = "rollup" + (extraCls ? " " + extraCls : "");
  if (streamIndex != null) span.dataset.streamIndex = String(streamIndex);
  span.textContent = text;
  if (title) span.title = title; // "" → the cell's own hover card explains it; skip, don't set title=""
  return span;
}

// The days behind a group's total, for hovering: how much work it holds, how much is finished and
// how much is still to do. `left` pairs with rollup (not m.remaining) so total = done + left even
// when a group's own estimate stands in for un-estimated children. Returned as rows so the hover
// card can align the numbers; `notes` are the plain sentences under them.
interface EstBreakdown { rows: [string, string][]; notes: string[] }
function estBreakdown(m: NodeMetrics): EstBreakdown {
  const left = Math.max(0, m.rollup - m.doneDays);
  const rows: [string, string][] = [
    ["Total", fmt(m.rollup) + "d"],
    ["Done", fmt(m.doneDays) + "d"],
    ["Left", fmt(left) + "d"],
  ];
  const notes: string[] = [];
  if (m.totalN) notes.push(m.doneN + " of " + m.totalN + (m.totalN === 1 ? " task" : " tasks") + " done");
  if (m.unestimated) notes.push(m.unestimated + (m.unestimated === 1 ? " task" : " tasks") + " not estimated yet");
  return { rows, notes };
}

// Hang the breakdown on the CELL (not the number) so it also covers the "+N unestimated" badge and
// the padding. Shown as the same dark floating card as comments/tags — a native `title` truncated
// the split into one grey line and took a second to appear. `tipBase` (what the number in the cell
// means) and the metrics are stashed on the element, so hovering reads current values and
// updateDerived only has to re-stash them after an estimate edit moves the split.
function applyEstTip(td: HTMLElement, tipBase: string, m: NodeMetrics): void {
  td.classList.add("est-tip");
  td.dataset.tipBase = tipBase;
  estTipData.set(td, m);
  td.onmouseenter = () => showEstHover(td);
  td.onmouseleave = () => hideEstHover();
  if (estHoverTd === td) showEstHover(td); // card open on this cell → redraw it with the new split
}

/* ---------- group-total hover card (the done/left day split) ---------- */
const estTipData = new WeakMap<HTMLElement, NodeMetrics>();
let estHover: HTMLElement | null = null;
let estHoverTd: HTMLElement | null = null;

function showEstHover(td: HTMLElement): void {
  hideEstHover();
  const m = estTipData.get(td);
  if (!m) return;
  const { rows, notes } = estBreakdown(m);
  const pop = document.createElement("div");
  pop.className = "tag-hover est-hover";
  rows.forEach(([label, value]) => {
    const row = document.createElement("div");
    row.className = "est-hover-row";
    const k = document.createElement("span"); k.textContent = label;
    const v = document.createElement("span"); v.className = "v"; v.textContent = value;
    row.append(k, v);
    pop.appendChild(row);
  });
  notes.forEach((n, i) => {
    const el = document.createElement("div");
    el.className = "est-hover-note" + (i === 0 ? " first" : ""); // `first` draws the divider rule
    el.textContent = n;
    pop.appendChild(el);
  });
  const base = td.dataset.tipBase || "";
  if (base) {
    const hint = document.createElement("div");
    hint.className = "tag-hover-hint";
    hint.textContent = base;
    pop.appendChild(hint);
  }
  document.body.appendChild(pop);
  positionCard(pop, td);
  estHover = pop;
  estHoverTd = td;
}
function hideEstHover(): void {
  if (estHover) { estHover.remove(); estHover = null; }
  estHoverTd = null;
}

// fill a Σ span: the number as text + a dimmed "d" unit sub-span so the value reads clearly.
// Group rows leave a 0 total blank.
function fillSum(span: HTMLElement, rollup: number, isGroup: boolean, withUnit: boolean): void {
  span.textContent = "";
  if (isGroup && !rollup) return;
  span.textContent = fmt(rollup);
  if (!withUnit) return; // sum-only mode: header already reads "Est", so drop the cell "d"
  const u = document.createElement("span");
  u.className = "unit";
  u.textContent = "d";
  span.appendChild(u);
}

// fill a group's plain roll-up span: "Nd" with a slightly-detached dim "d" unit; a 0 stays blank.
function fillRollup(span: HTMLElement, value: number): void {
  span.textContent = "";
  if (!value) return;
  span.textContent = fmt(value);
  const u = document.createElement("span");
  u.className = "unit";
  u.textContent = "d";
  span.appendChild(u);
}

// an editable estimate input for a leaf — either the whole estimate (streamKey null) or one
// named stream. `editStreams` is the row's ordered editable stream keys so Tab can step through
// all of them (UI → Backend → …) before returning to the task name.
function buildEstInput(node: TaskNode, streamKey: string | null, editStreams: string[]): HTMLInputElement {
  const est = document.createElement("input");
  est.className = "est";
  est.type = "number";
  est.min = "0";
  est.step = "0.5";
  if (streamKey != null) est.dataset.stream = streamKey;
  const write = (v: string | number) => streamKey != null
    ? setEstimateStream(state.tree, node.id, streamKey, v, state.estimations)
    : setEstimate(state.tree, node.id, v);
  const cur = streamKey != null ? (node.estimates[streamKey] ?? 0) : node.estimate;
  est.value = cur ? String(cur) : ""; // show 0 as blank to avoid a wall of zeros
  est.oninput = () => { write(est.value); updateDerived(); };
  est.onblur = () => save();
  est.onkeydown = (e) => {
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); est.blur(); }
    else if (e.key === "Enter") { e.preventDefault(); focusName(node.id, false); }
    else if (e.key === "Tab") {
      e.preventDefault();
      const i = streamKey ? editStreams.indexOf(streamKey) : -1;
      const to = e.shiftKey ? (i > 0 ? editStreams[i - 1] : undefined) : editStreams[i + 1];
      if (to) focusEst(node.id, to); else focusName(node.id, false); // past the ends → back to the name
    }
    else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      const up = e.key === "ArrowUp";
      if (e.altKey) {
        const cur = parseFloat(est.value) || 0;
        const next = Math.max(0, Math.round((cur + (up ? 0.5 : -0.5)) * 100) / 100);
        est.value = String(next);
        write(next);
        updateDerived();
      } else focusEst(nextEstId(node.id, up ? -1 : 1, streamKey ?? undefined), streamKey ?? undefined);
    }
  };
  return est;
}

/* ---------- tree view ---------- */
function renderInner(): void {
  const { rows, sched, gridBg, tlWidth, estCols } = computeTimeline();
  state.visibleOrder = rows.map((r) => r.node.id);
  const sectionDepth = rootIsSolo(state.tree) ? 1 : 0;
  const leafNo = leafIndexMap(state.tree);
  const metrics = computeMetrics(state.tree); // one bottom-up pass; O(1) lookup per row below
  const deps = state.countDeps ? dependencyExtras(state.tree, metrics) : null; // display-only extras
  const streamRollups = state.estimations.length ? computeStreamRollups(state.tree, state.estimations) : null;
  const shadowVers = state.versions.length ? computeShadowVersions(state.tree, state.versions) : null; // versionless-group hint
  const fRollups = filteredLeafRollups(); // non-null under a pinned/version filter → group "shown of total"
  refreshTagList();
  leftEmpty.hidden = rows.length > 0;
  if (rows.length === 0) {
    // distinguish "no tasks at all" from "a filter hid everything"
    if (state.tree.length && (filterMatch() || state.hideDone)) {
      leftEmpty.textContent = state.hideDone && !filterMatch()
        ? "Everything's done — nothing to show. Turn off “Hide done” to see completed tasks."
        : state.showPinnedOnly && !state.versionFilter
          ? "No pinned tasks. Pin one with 📌, or turn off the filter."
          : state.versionFilter && !state.showPinnedOnly
            ? (state.versionFilter === "*" ? "No tasks have a version yet." : `No tasks in version ${state.versionFilter}.`)
            : "No tasks match the active filters.";
    } else {
      leftEmpty.innerHTML = "No tasks yet. Click <b>+ Task</b> to start.";
    }
  }
  populateBtn.style.display = state.tree.length ? "none" : "";
  tbody.innerHTML = "";

  rows.forEach(({ node, depth, wbs, path, disabled }) => {
    const tr = document.createElement("tr");
    tr.dataset.id = node.id;
    if (node.children.length) tr.classList.add("group");
    if (depth === sectionDepth && node.children.length) tr.classList.add("section");
    const m = metrics.get(node.id) as NodeMetrics;
    if (disabled) tr.classList.add("disabled");
    else if (m.done) tr.classList.add("done"); // green when complete, even if the completion column is hidden
    else if (node.children.length === 0 && m.unestimated > 0) tr.classList.add("unestimated"); // orange: open leaf, no estimate

    const hasKids = node.children.length > 0;
    // ordered editable stream keys on this row → Tab steps through every estimate input
    const editStreams = hasKids ? [] : estCols.filter((c) => c.kind === "stream").map((c) => c.key!);

    const tdLeaf = document.createElement("td");
    tdLeaf.className = "sticky-col col-leaf wbs";
    tdLeaf.textContent = hasKids ? "" : String(leafNo.get(node.id) ?? "");
    tr.appendChild(tdLeaf);

    const tdWbs = document.createElement("td");
    tdWbs.className = "sticky-col col-wbs wbs wbs-toggle";
    tdWbs.textContent = wbs;
    tdWbs.title = node.disabled ? "Click to enable this task & subtasks" : "Click to disable this task & subtasks";
    tdWbs.onclick = async () => {
      if (!node.children.length) { toggleDisabled(state.tree, node.id); commit(); return; }
      const scope = await pickScope(node, node.disabled ? "Enable" : "Disable");
      if (scope === "cancel") return;
      if (scope) { // visible-only: toggle the visible descendants (disable if any still enabled)
        let anyEnabled = false;
        (function walk(list: TaskNode[]) {
          for (const n of list) { if (scope.has(n.id) && !n.disabled) anyEnabled = true; walk(n.children); }
        })(node.children);
        setDisabledCascade(state.tree, node.id, anyEnabled, scope);
      } else {
        setDisabledCascade(state.tree, node.id, !node.disabled); // whole group (own flag + inheritance)
      }
      commit();
    };
    tr.appendChild(tdWbs);

    const tdName = document.createElement("td");
    tdName.className = "sticky-col col-task";
    const inner = document.createElement("div");
    inner.className = "name-inner";
    inner.style.paddingLeft = depth * 16 + "px";

    const toggle = document.createElement("button");
    toggle.className = "toggle" + (hasKids ? "" : " empty");
    toggle.textContent = node.collapsed ? "▶" : "▼";
    toggle.title = node.collapsed ? "Expand" : "Collapse";
    toggle.onclick = () => { toggleCollapse(state.tree, node.id); render(); }; // view-only: persisted to localStorage, not the server
    inner.appendChild(toggle);

    if (state.showDone) {
      const chk = document.createElement("input");
      chk.type = "checkbox";
      chk.className = "done-check";
      if (hasKids) {
        const c = m;
        chk.checked = c.totalN > 0 && c.doneN === c.totalN;
        chk.indeterminate = c.doneN > 0 && c.doneN < c.totalN;
        chk.disabled = disabled || c.totalN === 0; // only when there are active subtasks to mark
        chk.title = c.totalN ? "Mark subtasks done / not done (" + c.doneN + "/" + c.totalN + ")" : "no active subtasks";
        chk.onchange = async () => {
          const scope = await pickScope(node, "Mark done");
          if (scope === "cancel") { render(); return; } // restore the checkbox's derived state
          // mark done unless every in-scope active leaf is already done → then clear
          let total = 0, done = 0;
          (function walk(list: TaskNode[]) {
            for (const n of list) {
              if (n.disabled) continue;
              if (n.children.length === 0) { if (!scope || scope.has(n.id)) { total++; if (n.done) done++; } }
              else walk(n.children);
            }
          })(node.children);
          setDoneCascade(state.tree, node.id, !(total > 0 && done === total), scope);
          commit();
        };
      } else {
        chk.checked = !!node.done;
        chk.disabled = disabled;
        chk.title = "Mark done";
        chk.onchange = () => { setDone(state.tree, node.id, chk.checked); commit(); };
      }
      inner.appendChild(chk);
    }

    const name = document.createElement("input");
    name.className = "name";
    name.value = node.name;
    let nameBase = node.name; // the title this focus session started with — what Enter compares against
    name.onfocus = () => { nameBase = name.value; };
    name.oninput = () => setName(state.tree, node.id, name.value);
    name.onblur = () => {
      const trimmed = name.value.trim();
      if (trimmed !== name.value) { name.value = trimmed; setName(state.tree, node.id, trimmed); }
      if (state.freshId === node.id) state.freshId = null; // left the row: it's an ordinary task now
      save();
    };
    name.onkeydown = (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); name.blur(); }
      else if (e.key === "Enter") { e.preventDefault(); enterFromName(node.id, name, nameBase); }
      else if (e.key === "Backspace" && name.value === "" && node.children.length === 0) {
        e.preventDefault();
        const prev = siblingId(node.id, -1);
        removeTask(state.tree, node.id); commit();
        if (prev) focusName(prev, false);
      } else if (e.key === "Tab") {
        e.preventDefault();
        if (e.shiftKey) focusName(siblingId(node.id, -1), false);
        else focusEst(node.id, editStreams[0]); // → first estimate input (UI), then Tab walks the rest
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        if (e.altKey) { move(state.tree, node.id, 1); flash(node.id); commit(); focusName(node.id, false); }
        else focusName(siblingId(node.id, 1), false);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (e.altKey) { move(state.tree, node.id, -1); flash(node.id); commit(); focusName(node.id, false); }
        else focusName(siblingId(node.id, -1), false);
      } else if (e.altKey && (e.key === "ArrowRight" || e.key === "ArrowLeft")) {
        e.preventDefault();
        if (e.key === "ArrowRight" ? indent(state.tree, node.id) : outdent(state.tree, node.id)) { flash(node.id); save(); focusName(node.id, false); }
      }
    };
    inner.appendChild(name);

    buildTaskIcons(node).forEach((b) => inner.appendChild(b));

    if (state.showComplexity) {
      const cxLevel = node.complexity || "none";
      const cxBtn = document.createElement("button");
      cxBtn.className = "complexity cx-" + cxLevel;
      cxBtn.textContent = cxLevel === "hard" ? "●●●" : cxLevel === "medium" ? "●●" : cxLevel === "easy" ? "●" : "◦";
      cxBtn.title = "Complexity: " + cxLevel + " — click to cycle (none → easy → medium → hard)";
      cxBtn.onclick = (e) => { e.stopPropagation(); cycleComplexity(state.tree, node.id); commit(); };
      inner.appendChild(cxBtn);
    }

    const badge = buildJiraBadge(node);
    if (badge) inner.appendChild(badge);

    tdName.appendChild(inner);
    tr.appendChild(tdName);

    const tdCopy = document.createElement("td");
    tdCopy.className = "sticky-col col-copy";
    const copyBtn = document.createElement("button");
    copyBtn.className = "copy-btn";
    copyBtn.textContent = "⧉";
    copyBtn.title = "Copy Jira key + path";
    copyBtn.onclick = () => {
      const key = node.jiraKey ? node.jiraKey + " " : ""; // "KEY A > B > C" (path only when no key)
      copyToClipboard(key + currentPath(state.tree, node.id), copyBtn, "✓");
    };
    tdCopy.appendChild(copyBtn);
    tr.appendChild(tdCopy);

    const tdTags = document.createElement("td");
    tdTags.className = "sticky-col col-tags";
    tdTags.appendChild(buildTagsCell(node));
    tr.appendChild(tdTags);

    const tdVer = document.createElement("td");
    tdVer.className = "sticky-col col-ver";
    const verSel = buildVersionSelect(node);
    // versionless group → faintly show the latest version among its subtasks (indicator only)
    const sv = hasKids && !node.version && shadowVers ? (shadowVers.get(node.id) || "") : "";
    if (sv) {
      verSel.options[0].text = ""; // hide the "—" so the hint shows through the empty select
      const wrap = document.createElement("div");
      wrap.className = "ver-cell";
      const hint = document.createElement("span");
      hint.className = "ver-shadow";
      hint.textContent = sv;
      hint.title = "Latest version among subtasks — not assigned";
      wrap.append(verSel, hint);
      tdVer.appendChild(wrap);
    } else {
      tdVer.appendChild(verSel);
    }
    tr.appendChild(tdVer);

    const tdComp = document.createElement("td");
    tdComp.className = "sticky-col col-comp";
    tdComp.appendChild(buildComponentSelect(node));
    tr.appendChild(tdComp);

    // one cell per estimate column (plain / per-stream / Σ). Leaves get editable inputs; parents
    // (and the Σ) always show read-only roll-ups — a group's estimate is derived from its subtasks,
    // never edited directly (even when every subtask is still 0/empty). Matches the stream columns.
    const gfmt = (v: number) => (v ? fmt(v) : ""); // group rows: leave 0 roll-ups blank, not "0"
    estCols.forEach((c, ci) => {
      const td = document.createElement("td");
      td.className = "sticky-col col-est" + (c.kind === "sum" ? " col-est-sum" : "");
      td.style.setProperty("--l-est", "var(--l-est-" + ci + ")"); // tracks resizes, see computeTimeline
      const isLast = c === estCols[estCols.length - 1];
      const tip = hasKids && isLast; // a group's total column → hover it for the done/left split
      let tipBase = "";              // first tooltip line: what the number in the cell is
      if (hasKids && isLast && fRollups) {
        // filtered view: a group's total column shows the SHOWN tasks' estimate "of" its full
        // total — e.g. "10d of 127d" ⇒ 10d of pinned work within a 127d group. When all of the
        // group is shown (shown == total) the "of Nd" is redundant, so just the number is drawn.
        const shown = fRollups.get(node.id) || 0;
        const span = document.createElement("span");
        span.className = "rollup-of";
        span.textContent = fmt(shown) + "d";
        if (shown !== m.rollup) {
          const rest = document.createElement("span");
          rest.className = "of-total"; // faded "shadow" — same size, just muted
          rest.textContent = " of " + fmt(m.rollup) + "d";
          span.appendChild(rest);
        }
        tipBase = "Estimate of the shown tasks / this group's full total";
        td.appendChild(span);
      } else if (c.kind === "sum") {
        // Σ: leaves show their own total; groups blank a 0. Number + dimmed "d" unit.
        tipBase = "Σ total — drives the timeline";
        const span = rollupSpan("", tip ? "" : tipBase, null, "sum-val");
        fillSum(span, m.rollup, hasKids, true);
        td.appendChild(span);
      } else if (c.kind === "stream" && hasKids) {
        const v = streamRollups ? (streamRollups.get(node.id)?.[c.index] ?? 0) : 0;
        td.appendChild(rollupSpan(gfmt(v), "Rolled up from subtasks", c.index)); // never the last column
      } else if (c.kind === "plain" && hasKids) {
        tipBase = "Rolled up from subtasks";
        const span = rollupSpan("", tip ? "" : tipBase, null); // group total → "Nd"
        fillRollup(span, m.rollup);
        td.appendChild(span);
      } else if (c.kind === "plain") {
        // editable leaf estimate: a plain number to type, with a slightly-detached "d" (shown when set)
        const input = buildEstInput(node, null, editStreams);
        const wrap = document.createElement("div");
        wrap.className = "est-edit";
        const u = document.createElement("span");
        u.className = "unit";
        u.textContent = "d";
        const syncU = () => { u.style.display = input.value ? "" : "none"; };
        syncU();
        input.addEventListener("input", syncU);
        wrap.append(input, u);
        td.appendChild(wrap);
      } else {
        td.appendChild(buildEstInput(node, c.kind === "stream" ? c.key : null, editStreams));
      }
      // on a group's total column (always the last estimate column), note how many descendant
      // tasks are still open with no estimate — e.g. "4d +5" ⇒ 4 days estimated, 5 unestimated.
      // Suppressed under a filter (the cell already reads "shown of total").
      if (hasKids && isLast && !fRollups && m.unestimated > 0) td.appendChild(unestBadge(m.unestimated));
      if (tip) applyEstTip(td, tipBase, m);
      tr.appendChild(td);
    });

    // dependency "extra" gets its own column so it never wraps under the estimate
    const tdDeps = document.createElement("td");
    tdDeps.className = "sticky-col col-deps";
    const dx = deps?.get(node.id) ?? 0;
    if (dx > 0) {
      const de = document.createElement("span");
      de.className = "dep-extra";
      de.textContent = "+" + fmt(dx) + "d";
      de.title = "Extra work from dependencies — not counted in this task's estimate";
      tdDeps.appendChild(de);
    }
    tr.appendChild(tdDeps);

    const tdAct = document.createElement("td");
    tdAct.className = "sticky-col col-act actions";
    tdAct.appendChild(iconBtn("➕", "Add subtask", () => { const nid = addChild(state.tree, node.id, state.defaultEstimate); commit(); focusNewName(nid); }));
    tdAct.appendChild(iconBtn("⬆", "Move up (Alt+↑)", () => { move(state.tree, node.id, -1); flash(node.id); commit(); }));
    tdAct.appendChild(iconBtn("⬆", "Move down (Alt+↓)", () => { move(state.tree, node.id, 1); flash(node.id); commit(); }, 180));
    tdAct.appendChild(iconBtn("⬆", "Outdent (Alt+←)", () => { if (outdent(state.tree, node.id)) { flash(node.id); commit(); } }, -90));
    tdAct.appendChild(iconBtn("⬆", "Indent (Alt+→)", () => { if (indent(state.tree, node.id)) { flash(node.id); commit(); } }, 90));
    const del = iconBtn("🗑", "Delete", () => {
      if (node.children.length === 0 || confirm(`Delete "${node.name}" and its ${node.children.length} subtask(s)?`)) {
        removeTask(state.tree, node.id); commit();
      }
    });
    del.className = "del";
    tdAct.appendChild(del);
    tr.appendChild(tdAct);

    const s = sched.get(node.id);
    const tdTl = document.createElement("td");
    tdTl.className = "col-tl";
    tdTl.style.width = tlWidth + "px";
    tdTl.style.background = gridBg;
    if (s) {
      const bar = document.createElement("div");
      bar.className = "gantt-bar" + (s.isParent ? " parent" : "");
      const dur = s.end - s.start;
      bar.style.left = s.start * PX_PER_DAY + "px";
      bar.style.width = Math.max(dur * PX_PER_DAY, 3) + "px";
      bar.textContent = dur > 0 ? fmt(dur) + "d" : "";
      bar.title = `${trimPath(state.tree, path).join(" > ")}: day ${fmt(s.start + 1)} → ${fmt(s.end)} (${fmt(dur)}d)`;
      tdTl.appendChild(bar);
    }
    tr.appendChild(tdTl);

    tbody.appendChild(tr);
  });

  if (state.pendingFocus) {
    const tr = tbody.querySelector(`tr[data-id="${state.pendingFocus.id}"]`);
    if (tr) {
      const pf = state.pendingFocus;
      const estSel = pf.field === "est"
        ? (pf.stream ? tr.querySelector(`input.est[data-stream="${CSS.escape(pf.stream)}"]`) : tr.querySelector("input.est"))
        : null;
      const el = (estSel || tr.querySelector("input.name")) as HTMLInputElement | null;
      if (el) {
        el.focus();
        if (state.pendingFocus.select) el.select();
        else { const end = el.value.length; el.setSelectionRange(end, end); }
        el.scrollIntoView({ block: "nearest" });
      }
    }
    state.pendingFocus = null;
  }

  // one-shot: the class is applied to the freshly built row, then cleared, so the
  // animation plays exactly once and later renders don't retrigger it
  if (state.flashId) {
    const tr = tbody.querySelector(`tr[data-id="${state.flashId}"]`);
    if (tr) {
      tr.classList.add("moved");
      tr.scrollIntoView({ block: "nearest" });
    }
    state.flashId = null;
  }

  updateTotalLabel();
  updateProgress();
  applyTagHighlight();
}

/* ---------- by-version view ---------- */
function renderByVersion(): void {
  leftEmpty.hidden = true;
  populateBtn.style.display = state.tree.length ? "none" : "";
  versionView.classList.toggle("tags-on", state.showTags); // tags shown → rows fill the full width

  const items: { node: TaskNode; path: string[] }[] = [];
  (function walk(list: TaskNode[], ancestors: string[], ancestorDisabled: boolean) {
    list.forEach((n) => {
      const disabled = ancestorDisabled || !!n.disabled;
      const path = ancestors.concat(n.name);
      if (n.children.length === 0) { if (!disabled) items.push({ node: n, path }); }
      else walk(n.children, path, disabled);
    });
  })(state.tree, [], false);

  // Order tasks by the Gantt's dependency order (a blocker before the tasks it blocks), so within
  // each version the by-version list matches the timeline. Only when dependency counting is on —
  // otherwise the Gantt itself uses document order, which is what `items` already holds.
  if (state.countDeps) {
    const pos = new Map(dependencyLeafOrder(state.tree, state.depDocOrder).map((n, i) => [n.id, i] as const));
    items.sort((a, b) => (pos.get(a.node.id) ?? 0) - (pos.get(b.node.id) ?? 0));
  }

  const order = state.versions.slice();
  items.forEach((it) => { const v = it.node.version || ""; if (v && !order.includes(v)) order.push(v); });
  const leafNo = leafIndexMap(state.tree); // same leaf numbering as the tree view's leaf# column
  const wbsById = new Map<string, string>(); // WBS number for every node (full tree, ignores collapse)
  (function walk(list: TaskNode[], prefix: string) {
    list.forEach((n, i) => {
      const wbs = prefix ? prefix + "." + (i + 1) : String(i + 1);
      wbsById.set(n.id, wbs);
      walk(n.children, wbs);
    });
  })(state.tree, "");

  versionView.innerHTML = "";
  if (items.length === 0) {
    const note = document.createElement("div");
    note.className = "ver-empty";
    note.textContent = "No active tasks to plan — add tasks (and keep them enabled) to see them grouped by version.";
    versionView.appendChild(note);
    updateTotalLabel();
    return;
  }

  order.concat([""]).forEach((v) => {
    const groupItems = items.filter((it) => (it.node.version || "") === v);
    if (v === "" && groupItems.length === 0) return;

    const group = document.createElement("div");
    group.className = "ver-group";
    const head = document.createElement("div");
    head.className = "ver-group-head";
    const badge = document.createElement("span");
    badge.className = "ver-badge" + (v ? "" : " none");
    badge.textContent = v || "— no version";
    head.appendChild(badge);
    const sub = document.createElement("span");
    sub.className = "sub";
    // recomputed live while an estimate is being edited in this group
    const refreshSub = () => {
      const subtotal = groupItems.reduce((s, it) => s + (it.node.estimate || 0), 0);
      const doneDays = groupItems.reduce((s, it) => s + (it.node.done ? it.node.estimate || 0 : 0), 0);
      const pct = subtotal ? Math.round((doneDays / subtotal) * 100) : 0;
      sub.textContent = groupItems.length + " task" + (groupItems.length === 1 ? "" : "s") + " · " + fmt(subtotal) + "d"
        + (state.showDone && groupItems.length ? " · " + pct + "% done" : "");
    };
    refreshSub();
    head.appendChild(sub);
    group.appendChild(head);

    groupItems.forEach((it) => {
      const row = document.createElement("div");
      row.className = "ver-item" + (it.node.done ? " done" : (it.node.estimate ? "" : " unestimated")); // green done / orange unestimated
      const num = document.createElement("span");
      num.className = "ver-num wbs"; // leaf#, same muted style as the tree view
      num.textContent = String(leafNo.get(it.node.id) ?? "");
      row.appendChild(num);

      const wbsEl = document.createElement("span");
      wbsEl.className = "ver-wbs wbs"; // WBS number
      wbsEl.textContent = wbsById.get(it.node.id) ?? "";
      row.appendChild(wbsEl);

      if (state.showDone) {
        const chk = document.createElement("input");
        chk.type = "checkbox";
        chk.className = "done-check";
        chk.checked = !!it.node.done;
        chk.title = "Mark done";
        chk.onchange = () => { setDone(state.tree, it.node.id, chk.checked); commit(); };
        row.appendChild(chk);
      }
      const p = document.createElement("span");
      p.className = "path";
      const tp = trimPath(state.tree, it.path);
      const lead = tp.slice(0, -1).join(" › ");
      if (lead) { const ls = document.createElement("span"); ls.className = "lead"; ls.textContent = lead + " › "; p.appendChild(ls); }
      const leafSpan = document.createElement("span");
      leafSpan.className = "leaf";
      leafSpan.textContent = it.path[it.path.length - 1];
      p.appendChild(leafSpan);
      row.appendChild(p);

      // same quick actions as the tree view: flag questions, comment, pin
      buildTaskIcons(it.node).forEach((b) => row.appendChild(b));

      // copy "KEY A > B > C" (or just the path when there's no Jira key)
      const copyBtn = document.createElement("button");
      copyBtn.className = "copy-btn ver-copy";
      copyBtn.textContent = "⧉";
      copyBtn.title = "Copy Jira key + path";
      copyBtn.onclick = () => {
        const key = it.node.jiraKey ? it.node.jiraKey + " " : "";
        copyToClipboard(key + currentPath(state.tree, it.node.id), copyBtn, "✓");
      };
      row.appendChild(copyBtn);

      // same rich elements as the tree view, gated by the same View toggles
      if (state.showTags) {
        const tags = buildTagsCell(it.node);
        tags.classList.add("ver-tags");
        row.appendChild(tags);
      }
      const badge = buildJiraBadge(it.node);
      if (badge) row.appendChild(badge);
      if (state.showComponents) {
        const comp = buildComponentSelect(it.node);
        comp.classList.add("ver-pick");
        row.appendChild(comp);
      }

      const est = document.createElement("input");
      est.className = "est est-input";
      est.type = "text";
      est.inputMode = "decimal";
      est.title = "Estimate in days";
      const dispEst = () => (it.node.estimate ? fmt(it.node.estimate) + "d" : ""); // "2d" when idle
      est.value = dispEst();
      // live-update this group's subtotal + the header totals; persist on blur (no rebuild → keeps focus)
      const applyEst = (val: string | number) => { est.value = String(val); setEstimate(state.tree, it.node.id, val); refreshSub(); updateTotalLabel(); updateProgress(); };
      est.onfocus = () => { est.value = String(it.node.estimate); est.select(); }; // show the raw number to edit
      est.oninput = () => { setEstimate(state.tree, it.node.id, parseFloat(est.value) || 0); refreshSub(); updateTotalLabel(); updateProgress(); };
      est.onblur = () => { save(); est.value = dispEst(); }; // back to "2d"
      est.onkeydown = (e) => {
        if (e.key === "Escape" || e.key === "Enter") { e.preventDefault(); est.blur(); }
        else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
          e.preventDefault(); // own the ±0.5 step in both directions (native stepping is flaky with Alt)
          const cur = parseFloat(est.value) || 0;
          applyEst(Math.max(0, Math.round((cur + (e.key === "ArrowUp" ? 0.5 : -0.5)) * 100) / 100));
        }
      };
      row.appendChild(est);
      const pick = buildVersionSelect(it.node);
      pick.classList.add("ver-pick");
      row.appendChild(pick);
      group.appendChild(row);
    });
    versionView.appendChild(group);
  });

  updateTotalLabel();
  updateProgress();
}

/* ---------- tags view ---------- */
export function toggleActiveTag(tag: string): void {
  state.activeTag = state.activeTag === tag ? null : tag;
  applyTagHighlight();
}

function applyTagHighlight(): void {
  const matched = tagMatchSet(state.tree, state.activeTag);
  tbody.querySelectorAll("tr").forEach((tr) => {
    tr.classList.toggle("tag-match", matched.has((tr as HTMLElement).dataset.id || ""));
  });
  tbody.querySelectorAll(".chip").forEach((ch) => {
    ch.classList.toggle("active", !!state.activeTag && (ch as HTMLElement).dataset.tag === state.activeTag);
  });
}

function refreshTagList(): void {
  tagList.innerHTML = "";
  allTags(state.tree).forEach((t) => {
    const opt = document.createElement("option");
    opt.value = t;
    tagList.appendChild(opt);
  });
}

// The per-task action icons (flag questions / comment / pin), shared by the tree grid
// and the by-version view so both offer the same quick actions.
function buildTaskIcons(node: TaskNode): HTMLElement[] {
  const qBtn = document.createElement("button");
  qBtn.className = "q-toggle" + (node.questions ? " on" : "");
  qBtn.textContent = "?";
  qBtn.title = node.questions ? "Has open questions — click to clear" : "Flag open questions / TBD";
  qBtn.onclick = (e) => { e.stopPropagation(); toggleQuestions(state.tree, node.id); commit(); };

  const cBtn = document.createElement("button");
  cBtn.className = "c-toggle" + (node.comment ? " on" : "");
  cBtn.innerHTML = '<svg viewBox="0 0 16 16" aria-hidden="true"><path fill="currentColor" d="M3 2.5h10A1.5 1.5 0 0 1 14.5 4v6A1.5 1.5 0 0 1 13 11.5H7l-3 2.6V11.5H3A1.5 1.5 0 0 1 1.5 10V4A1.5 1.5 0 0 1 3 2.5z"/></svg>';
  cBtn.title = node.comment ? "" : "Add a comment / note";
  cBtn.onmouseenter = () => showCommentHover(node, cBtn);
  cBtn.onmouseleave = () => hideCommentHover();
  cBtn.onclick = (e) => {
    e.stopPropagation();
    hideCommentHover();
    if (commentPopId === node.id) closeCommentPop();
    else openCommentPop(node, cBtn);
  };

  const pBtn = document.createElement("button");
  pBtn.className = "pin-toggle" + (node.pinned ? " on" : "");
  pBtn.textContent = "📌";
  pBtn.title = node.pinned ? "Pinned — click to unpin" : "Pin this task";
  pBtn.onclick = (e) => { e.stopPropagation(); togglePinned(state.tree, node.id); commit(); };

  return [qBtn, cBtn, pBtn];
}

function buildTagsCell(node: TaskNode): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "chips";
  if (node.tag) wrap.appendChild(makeChip(node.tag, "own", () => { setTag(state.tree, node.id, ""); commit(); }));
  else wrap.appendChild(makeAddControl("＋ tag", (v) => { setTag(state.tree, node.id, v); commit(); }));
  (node.links || []).forEach((t) => wrap.appendChild(makeChip(t, "link", () => { removeLink(state.tree, node.id, t); commit(); })));
  wrap.appendChild(makeAddControl("🔗", (v) => { addLink(state.tree, node.id, v); commit(); }));
  return wrap;
}

function makeChip(tag: string, kind: "own" | "link", onRemove: () => void): HTMLElement {
  const c = tagColor(tag);
  const chip = document.createElement("span");
  chip.className = "chip " + kind;
  chip.dataset.tag = tag;
  if (kind === "own") { chip.style.background = c.solidBg; chip.style.color = c.solidFg; }
  else { chip.style.background = c.softBg; chip.style.color = c.softFg; chip.style.borderColor = c.border; }

  const lbl = document.createElement("span");
  lbl.className = "lbl";
  lbl.textContent = tag;
  if (kind === "own") {
    chip.onmouseenter = () => showTagHover(tag, chip);
    chip.onmouseleave = () => hideTagHover();
  } else {
    lbl.title = `Click to highlight • double-click to rename “${tag}”`;
  }
  let clickTimer: ReturnType<typeof setTimeout> | null = null;
  lbl.onclick = () => { if (clickTimer) clearTimeout(clickTimer); clickTimer = setTimeout(() => toggleActiveTag(tag), 180); };
  lbl.ondblclick = (e) => { e.preventDefault(); if (clickTimer) clearTimeout(clickTimer); startRename(chip, tag); };
  chip.appendChild(lbl);

  const x = document.createElement("span");
  x.className = "x";
  x.textContent = "×";
  x.title = kind === "own" ? "Clear tag" : "Remove link";
  x.onclick = (e) => { e.stopPropagation(); onRemove(); };
  chip.appendChild(x);
  return chip;
}

function makeAddControl(label: string, onCommit: (v: string) => void): HTMLElement {
  const btn = document.createElement("button");
  btn.className = "chip-add";
  btn.textContent = label;
  btn.title = label === "🔗" ? "Link to another tag" : "Set this task's tag";
  btn.onclick = () => {
    const input = document.createElement("input");
    input.className = "chip-input";
    input.setAttribute("list", "tagList");
    input.placeholder = "tag…";
    let done = false;
    const commitVal = () => {
      if (done) return; done = true;
      const v = input.value.trim();
      if (v) onCommit(v); else render();
    };
    input.onkeydown = (e) => {
      if (e.key === "Enter") { e.preventDefault(); commitVal(); }
      else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); done = true; render(); }
    };
    // picking a suggestion from the datalist fires input with this type → commit right away,
    // so a following Esc doesn't cancel a selection the user already made.
    input.oninput = (e) => { if ((e as InputEvent).inputType === "insertReplacementText") commitVal(); };
    input.onblur = () => commitVal();
    btn.replaceWith(input);
    input.focus();
  };
  return btn;
}

function startRename(chipEl: HTMLElement, oldTag: string): void {
  const input = document.createElement("input");
  input.className = "chip-input";
  input.setAttribute("list", "tagList");
  input.value = oldTag;
  let done = false;
  const commitVal = () => {
    if (done) return; done = true;
    const changed = renameTag(state.tree, oldTag, input.value);
    if (!changed) { render(); return; }
    if (state.activeTag === oldTag) state.activeTag = input.value.trim();
    commit();
  };
  input.onkeydown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); commitVal(); }
    else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); done = true; render(); }
  };
  input.onblur = () => commitVal();
  chipEl.replaceWith(input);
  input.focus();
  input.select();
}

/* ---------- tag-usage hover ---------- */
let tagHover: HTMLElement | null = null;
function showTagHover(tag: string, anchor: HTMLElement): void {
  hideTagHover();
  const paths = tagUsagePaths(state.tree, tag);
  const pop = document.createElement("div");
  pop.className = "tag-hover";
  const title = document.createElement("div");
  title.className = "tag-hover-title";
  title.textContent = tag + " — linked from " + paths.length + " task" + (paths.length === 1 ? "" : "s");
  pop.appendChild(title);
  (paths.length ? paths : ["(no tasks link to this tag)"]).forEach((p) => {
    const row = document.createElement("div");
    row.className = "tag-hover-row" + (paths.length ? "" : " muted");
    row.textContent = p;
    pop.appendChild(row);
  });
  document.body.appendChild(pop);
  positionCard(pop, anchor);
  tagHover = pop;
}
function hideTagHover(): void { if (tagHover) { tagHover.remove(); tagHover = null; } }

/* ---------- version / component selects (same behavior, different field) ---------- */
function buildVersionSelect(node: TaskNode): HTMLSelectElement {
  return buildFieldSelect(node, "version", state.versions, setVersion, {
    label: "version", assign: "Assign a target version", set: "Target version ",
  });
}
function buildComponentSelect(node: TaskNode): HTMLSelectElement {
  return buildFieldSelect(node, "component", state.components, setComponent, {
    label: "component", assign: "Assign a component", set: "Component ",
  });
}

function buildFieldSelect(
  node: TaskNode,
  field: "version" | "component",
  options: string[],
  setter: (tree: TaskNode[], id: string, value: string, mode?: "matching" | "all", scope?: Set<string>) => void,
  labels: { label: string; assign: string; set: string },
): HTMLSelectElement {
  const cur = node[field];
  const sel = document.createElement("select");
  sel.className = "ver-select" + (cur ? " set" : "");
  sel.appendChild(new Option("—", ""));
  options.forEach((v) => sel.appendChild(new Option(v, v)));
  if (cur && !options.includes(cur)) sel.appendChild(new Option(cur + " (?)", cur));
  sel.value = cur || "";
  sel.title = cur ? labels.set + cur : labels.assign;
  sel.onchange = async () => {
    const newV = sel.value;
    const revert = () => { sel.value = cur || ""; };
    if (node.children.length) {
      // 1) a filter hides some descendants → apply to visible only or the whole group?
      const scope = await pickScope(node, "Set " + labels.label);
      if (scope === "cancel") { revert(); return; }
      // 2) the chosen scope still has values that make matching-vs-all differ → ask which
      if (matchingWouldDiffer(node, field, scope, newV)) {
        const mode = await askChoice({
          title: `Set ${labels.label} — which subtasks?`,
          message: `Some subtasks in scope are on a different ${labels.label}.`,
          choices: [
            { label: "Every task in scope", value: "all", primary: true },
            { label: `Only ones on "${cur || "—"}"`, value: "matching" },
          ],
        });
        if (mode === null) { revert(); return; }
        setter(state.tree, node.id, newV, mode as "matching" | "all", scope);
        commit();
        return;
      }
      setter(state.tree, node.id, newV, "all", scope);
    } else {
      setter(state.tree, node.id, newV);
    }
    commit();
  };
  return sel;
}

/* ---------- comments (hover preview + inline editor) ---------- */
let commentPop: HTMLElement | null = null;
let commentPopId: string | null = null;
let commentHover: HTMLElement | null = null;

export function commentIsOpen(): boolean { return commentPop !== null; }

function showCommentHover(node: TaskNode, anchor: HTMLElement): void {
  hideCommentHover();
  if (!node.comment || commentPopId === node.id) return;
  const pop = document.createElement("div");
  pop.className = "tag-hover comment-hover";
  const title = document.createElement("div");
  title.className = "tag-hover-title";
  title.textContent = node.name || "(untitled)";
  pop.appendChild(title);
  const body = document.createElement("div");
  body.className = "comment-body";
  body.textContent = node.comment;
  pop.appendChild(body);
  const hint = document.createElement("div");
  hint.className = "tag-hover-hint";
  hint.textContent = "Click to edit";
  pop.appendChild(hint);
  document.body.appendChild(pop);
  positionCard(pop, anchor);
  commentHover = pop;
}
function hideCommentHover(): void { if (commentHover) { commentHover.remove(); commentHover = null; } }

function openCommentPop(node: TaskNode, anchor: HTMLElement): void {
  hideCommentHover();
  closeCommentPop();
  const pop = document.createElement("div");
  pop.className = "comment-pop";
  const title = document.createElement("div");
  title.className = "comment-pop-title";
  title.textContent = node.name || "(untitled)";
  pop.appendChild(title);
  const ta = document.createElement("textarea");
  ta.rows = 6;
  ta.value = node.comment || "";
  ta.placeholder = "Add a comment / note…";
  pop.appendChild(ta);
  document.body.appendChild(pop);
  positionCard(pop, anchor);
  commentPop = pop;
  commentPopId = node.id;
  ta.focus();
}

export function closeCommentPop(persist = true): void {
  if (!commentPop) return;
  const val = (commentPop.querySelector("textarea") as HTMLTextAreaElement).value;
  const id = commentPopId;
  commentPop.remove();
  commentPop = null;
  commentPopId = null;
  if (persist && id) {
    const loc = locateOrNull(state.tree, id);
    if (loc && (loc.node.comment || "") !== val) { loc.node.comment = val; commit(); }
  }
}

document.addEventListener("mousedown", (e) => {
  const t = e.target as HTMLElement;
  if (commentPop && !commentPop.contains(t) && !t.closest(".c-toggle")) closeCommentPop();
});
// scroll/resize move the anchor out from under any floating card (fixed-position), so drop them
window.addEventListener("resize", () => { closeCommentPop(); hideEstHover(); });
gridWrap.addEventListener("scroll", () => { closeCommentPop(); hideEstHover(); }, { passive: true });

// Rows are deliberately not draggable: row-wide DnD fired on accidental click-and-drift
// and silently reparented tasks. Use the ⬆/⬇/indent/outdent buttons or Alt+arrows instead.

// keep the timeline stretched to the viewport on window resize
let resizeRaf = 0;
window.addEventListener("resize", () => {
  cancelAnimationFrame(resizeRaf);
  resizeRaf = requestAnimationFrame(() => { if (state.view === "tree" && state.visibleOrder.length) updateDerived(); });
});
